import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";

import { AjsResponse, wrapAjs } from "../ajs-response";
import { buildParams } from "../http-params.util";

/**
 * ThemesApiService (Angular 18).
 *
 * Replaces apps/b2b-ui/common/services/api/themesApi/themesApi.service.js.
 *
 * Manages tenant theme variables, links, ticket config, and membership
 * config. `detail(includeEmail)` adds `?includeEmail=true` when requested,
 * and `updateEmail()` always sends `?emailOnly=true` (used to restrict the
 * write to email-template variables only).
 */
const THEMES_BASE_V2 = "/v2/api/themes";

interface ThemePayload {
  variables: unknown;
  textVariables: unknown;
  links: unknown;
  emailVariables: unknown;
  membershipConfig: unknown;
  customScripts: unknown;
}

@Injectable({ providedIn: "root" })
export class ThemesApiService {
  private readonly http = inject(HttpClient);

  detail(includeEmail?: boolean): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(THEMES_BASE_V2, {
        params: includeEmail
          ? buildParams({ includeEmail: "true" })
          : undefined,
      }),
    );
  }

  update(props: ThemePayload): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.put<unknown>(THEMES_BASE_V2, {
        variables: props.variables,
        textVariables: props.textVariables,
        links: props.links,
        emailVariables: props.emailVariables,
        membershipConfig: props.membershipConfig,
        customScripts: props.customScripts,
      }),
    );
  }

  updateEmail(props: {
    emailVariables: unknown;
  }): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.put<unknown>(
        THEMES_BASE_V2,
        { emailVariables: props.emailVariables },
        { params: buildParams({ emailOnly: "true" }) },
      ),
    );
  }

  remove(): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.delete<unknown>(THEMES_BASE_V2));
  }

  getTicketConfig(): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${THEMES_BASE_V2}/tickets`));
  }

  updateTicketConfig(props: {
    ticketConfig: unknown;
  }): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.put<unknown>(`${THEMES_BASE_V2}/tickets`, {
        ticketConfig: props.ticketConfig,
      }),
    );
  }

  getMembershipConfig(): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${THEMES_BASE_V2}/membership`));
  }

  updateMembershipConfig(props: {
    membershipConfig: unknown;
  }): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.put<unknown>(`${THEMES_BASE_V2}/membership`, {
        membershipConfig: props.membershipConfig,
      }),
    );
  }
}
